import { CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop'
import { Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { PersonaField } from '@app/modules/personas/interfaces/persona-field.interface'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { catchError, of } from 'rxjs'
import { LegacyPersonaWizardContentComponent } from '../legacy-persona-wizard-content/legacy-persona-wizard-content.component'
import { PersonaService } from '../services/persona.service'
import { PersonaDetail } from '../types/persona.type'
import { buildFrequentlyUsedFields, getFormFieldByTypeId } from './data/form-field-library.data'
import { FormFieldLibraryItem } from './data/form-field-library.types'
import { FieldsLibraryComponent } from './fields-library/fields-library.component'
import { FormPreviewFieldComponent } from './form-preview-field/form-preview-field.component'

export enum PersonaPreviewMode {
  Desktop = 'desktop',
  Tablet = 'tablet',
  Mobile = 'mobile'
}

@Component({
  selector: 'app-manage-persona',
  standalone: true,
  imports: [CdkDropList, FieldsLibraryComponent, FormPreviewFieldComponent, ModalComponent, NgxTippyModule],
  providers: [PersonaService],
  templateUrl: './manage-persona.component.html',
  styleUrl: './manage-persona.component.scss',
  host: {
    class: 'tw-block tw-h-full'
  }
})
export class ManagePersonaComponent implements OnInit {
  readonly eventId = input.required<number>()
  readonly organizationId = input.required<number>()
  readonly personaId = input.required<number>()
  readonly eventName = input('')
  readonly personaName = input('')

  public readonly frequentlyUsed = signal<FormFieldLibraryItem[]>(buildFrequentlyUsedFields(null))
  public readonly fieldsLibraryModalId = crypto.randomUUID()
  public readonly isFieldsLibraryModalOpen = signal(false)
  public readonly isCanvasLoading = signal(true)
  public readonly isFrequentlyUsedLoading = signal(true)
  public readonly canvasFields = signal<PersonaField[]>([])
  public readonly previewPlaceholders = signal<Record<string, string>>({})
  public readonly PreviewMode = PersonaPreviewMode
  public readonly previewModes = [PersonaPreviewMode.Desktop, PersonaPreviewMode.Tablet, PersonaPreviewMode.Mobile] as const
  public readonly previewViewLabel: Record<PersonaPreviewMode, string> = {
    [PersonaPreviewMode.Desktop]: 'Desktop view',
    [PersonaPreviewMode.Tablet]: 'Tablet view',
    [PersonaPreviewMode.Mobile]: 'Mobile view'
  }
  public readonly previewMode = signal(PersonaPreviewMode.Desktop)
  /** Sum of library completion estimates for the fields currently on the canvas. */
  public readonly completionTimeSeconds = computed(() =>
    this.canvasFields().reduce((total, field) => total + (getFormFieldByTypeId(field.form_element_type_id)?.estimated_filling_time_seconds ?? 15), 0)
  )

  private readonly _personaService = inject(PersonaService)
  private readonly _wizardService = inject(WizardService)
  private readonly _modalService = inject(ModalService)
  private readonly _destroyRef = inject(DestroyRef)

  public ngOnInit(): void {
    this._personaService
      .getPersona(this.organizationId(), this.eventId(), this.personaId())
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((persona) => {
        this.hydrateCanvas(persona?.data)
        this.isCanvasLoading.set(false)
      })

    this._personaService
      .getFrequentlyUsedFields(this.organizationId())
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((res) => {
        this.frequentlyUsed.set(buildFrequentlyUsedFields(res?.data ?? null))
        this.isFrequentlyUsedLoading.set(false)
      })
  }

  /**
   * Opens the legacy manage-persona editor (previous form builder).
   */
  public onNext(): void {
    this._wizardService.open({
      component: LegacyPersonaWizardContentComponent,
      componentInputs: {
        eventId: this.eventId(),
        personaId: this.personaId()
      },
      headerConfig: {
        breadcrumbs: [this.eventName(), this.personaName()].filter(Boolean)
      },
      // Replace Manage Persona so X closes wizard / returns to personas list — not this shell.
      replaceTop: true
    })
  }

  public openFieldsLibrary(): void {
    if (this.isFieldsLibraryModalOpen()) return

    this.isFieldsLibraryModalOpen.set(true)
    this._modalService.toggleModal(this.fieldsLibraryModalId)
  }

  public closeFieldsLibrary(): void {
    if (this._modalService.isModalOpen(this.fieldsLibraryModalId)) this._modalService.toggleModal(this.fieldsLibraryModalId)
    this.isFieldsLibraryModalOpen.set(false)
  }

  /** Adds a local preview. Persisting field changes belongs to the future save flow. */
  public onFieldSelect(field: FormFieldLibraryItem): void {
    this.canvasFields.update((fields) => [
      ...fields,
      {
        form_element_type_id: field.form_element_type_id,
        label: field.name,
        name: field.name,
        weight: fields.length,
        is_required: 0,
        is_unique: false,
        is_disable_online: false,
        is_disable_onsite: false
      } as PersonaField
    ])
  }

  public onMobileFieldSelect(field: FormFieldLibraryItem): void {
    this.onFieldSelect(field)
    this.closeFieldsLibrary()
  }

  /** Library icon class for a canvas field. */
  public fieldIcon(field: PersonaField): string {
    return `fl-icon fl-icon-${getFormFieldByTypeId(field.form_element_type_id)?.icon ?? 'translate'}`
  }

  /**
   * Loads canvas fields only from GET .../personas/{id}/ (`data.form.formElements`).
   * Ordered by weight.
   */
  private hydrateCanvas(persona: PersonaDetail | null | undefined): void {
    const fields = persona?.form?.formElements ?? persona?.form?.form_elements ?? []
    this.canvasFields.set([...fields].sort((a, b) => (a.weight ?? 0) - (b.weight ?? 0)))
  }

  /** Preview placeholder: a local edit, otherwise the library default. */
  public fieldPlaceholder(field: PersonaField): string {
    return this.previewPlaceholders()[this.fieldKey(field)] ?? getFormFieldByTypeId(field.form_element_type_id)?.placeholder ?? 'Enter value'
  }

  /** Updates the canvas label. Not persisted until save. */
  public updateFieldLabel(field: PersonaField, label: string): void {
    this.canvasFields.update((fields) => fields.map((item) => (this.fieldKey(item) === this.fieldKey(field) ? { ...item, label } : item)))
  }

  /** Stores a preview-only placeholder. Not written back to the field. */
  public updateFieldPlaceholder(field: PersonaField, placeholder: string): void {
    this.previewPlaceholders.update((placeholders) => ({ ...placeholders, [this.fieldKey(field)]: placeholder }))
  }

  /** Stable identity for a canvas field before it has a server id. */
  public fieldKey(field: PersonaField): string {
    return String(field.id ?? `${field.name}-${field.weight}`)
  }

  /** Derived from the current canvas fields and their library time estimates. */
  public completionTimeLabel(): string {
    const seconds = this.completionTimeSeconds()
    if (seconds < 60) return `~ ${seconds} sec.`

    const minutes = Math.ceil(seconds / 60)
    return `~ ${minutes} min${minutes === 1 ? '.' : 's.'}`
  }

  /** Green under a minute, red at four minutes or more, otherwise orange. */
  public completionTimeTone(): 'green' | 'orange' | 'red' {
    const seconds = this.completionTimeSeconds()
    if (seconds <= 60) return 'green'
    if (Math.ceil(seconds / 60) >= 4) return 'red'
    return 'orange'
  }

  /** Canvas ordering is already state-owned, ready for CDK drag handles in the next iteration. */
  public reorderCanvasFields(event: CdkDragDrop<PersonaField[]>): void {
    if (event.previousIndex === event.currentIndex) return

    this.canvasFields.update((fields) => {
      const reordered = [...fields]
      moveItemInArray(reordered, event.previousIndex, event.currentIndex)
      return reordered.map((field, index) => ({ ...field, weight: index }))
    })
  }
}
